import { announceSelect2ChangesNatively } from "#/admin/legacy/jquery-change-bridge.js";
import { initializePatientContactFacilityFields } from "#/admin/legacy/forms/patient-contact-facility-fields";
import { initializeEmbedPreviews } from "#/admin/legacy/forms/embed-preview.js";
import { disposeFormBehaviors, initializeFormBehaviors } from "#/admin/legacy/forms/form-behavior-lifecycle";
import { initializeSelectItems } from "#/admin/legacy/sp-select/load-select-items.js";
import { settleMount } from "#ce/framework/mount-ready";
import "#legacy/shared/components/date_field.js";
import { controlsWithOptionTargets } from "#legacy/admin/behaviours/show_on_select.js";
import "./helper.js";

// FORM_CONTROL_SELECTOR adds the custom form fields to jQuery's `:input`, which matches only the
// native form tags. Each submits a value of its own, so a sweep that disables or snapshots a form's
// controls has to reach them.
var FORM_CONTROL_SELECTOR = ":input, sp-date-field, sp-select";

window.Form = Object.assign(window.Form || {}, {
  // VITE_MIGRATION: merge instead of replace to preserve glob-loaded submodules (FieldCounter, Errors)
  FORM_CONTROL_SELECTOR: FORM_CONTROL_SELECTOR,
  disposeBehaviors: disposeFormBehaviors,

  decorate: function (container) {
    container = jQuery(container || window.document);
    this.FieldCounter.decorate(container.find("[data-recommended-max], :input[maxlength]"));
    this.decorateDatePickers(container);
    this.decorateSelects(container);
    this.initShowOnSelect(container);
    initializeEmbedPreviews(container[0]);
    initializePatientContactFacilityFields(container[0]).catch(console.error);
    initializeFormBehaviors(container[0]).catch(console.error);
    this.initShowOnChecked(container);
    this.initToggleGroups(container);
    this.initNestedInserts(container);
    this.decorateFocusGroups(container);
    var decorateEditorPromise = this.decorateWYSIWYGs(container);
    return jQuery.when(decorateEditorPromise);
  },

  decorateDatePickers: function (container) {
    Sparkle.DateField.replaceAll(container);
  },

  decorateSelects: function (container) {
    const root = container?.nodeType ? container : container?.[0];

    initializeSelectItems(root);

    var me = this;

    jQuery(container)
      .find("[data-behaviour~=select2]")
      .not(".select2-hidden-accessible")
      .each(function () {
        me.decorateSelect2(this);
      });
  },

  decorateSelect2: function (select, options) {
    function formatValueWithGroupLabel(value) {
      var $value = jQuery(value.element);
      var $group = $value.closest("optgroup");
      var groupLabel = $group.attr("label");

      var $content = jQuery(
        '<span><span class="select2-selection__group-label"></span><span class="select2-selection__group-value"></span></span>',
      );
      $content.find(".select2-selection__group-label").text(groupLabel);
      $content.find(".select2-selection__group-value").text(jQuery(value.element).text());
      return $content;
    }

    select = select?.nodeType ? select : select?.[0];

    var defaults = {
      dropdownParent: jQuery(select).parent(),
      allowClear: jQuery("option", select).filter(function () {
        return select.value == "";
      }).length,
      width: "resolve",
      templateSelection: jQuery("optgroup", select).length ? formatValueWithGroupLabel : undefined,
    };

    var options = jQuery.extend({}, defaults, jQuery(select).data("select2Options"), options);

    // Add a placeholder to select2's search
    if (!jQuery(select).data("select2")) {
      jQuery(select).on("select2:open", function () {
        jQuery(".select2-search__field").attr("placeholder", "Type to search");
      });
    }

    announceSelect2ChangesNatively(select);
    jQuery(select).select2(options);
  },

  initShowOnSelect: function (container) {
    const root = container?.nodeType ? container : container?.[0];

    if (!root) {
      return Promise.resolve([]);
    }

    function announceSelection(control) {
      control.dispatchEvent(new Event("change", { bubbles: true }));
    }

    async function initializeReadyControl(control) {
      const mounted = await settleMount(control);

      if (mounted && control.isConnected && controlsWithOptionTargets(root).includes(control)) {
        announceSelection(control);
      }
    }

    controlsWithOptionTargets(root)
      .filter((control) => control.localName !== "sp-select")
      .forEach(announceSelection);

    return Promise.all(Array.from(root.querySelectorAll("sp-select"), initializeReadyControl));
  },

  initShowOnChecked: function (container) {
    const root = container?.nodeType ? container : container?.[0];

    root
      ?.querySelectorAll('[data-behaviour~="show-target-on-checked"]')
      .forEach(function initializeCheckedTarget(control) {
        control.dispatchEvent(new Event("change", { bubbles: true }));
      });
  },

  initNestedInserts: function (container) {
    var selector = "[data-behaviour~=nested-insert-container].nested-insert-with-associations";
    jQuery(container)
      .find(selector)
      .each(function (index, el) {
        jQuery(document).trigger("nested-inserts:setup", el);
      });
  },

  initToggleGroups: function (container) {
    // Toggle groups disable internal inputs when they are hidden. We want to also do this automatically when they are first added to the page
    container.find("[data-toggle-group-name]").each(function () {
      if (jQuery(this).css("display") == "none") {
        jQuery(this).find(FORM_CONTROL_SELECTOR).attr("disabled", true);
      }
    });
  },

  decorateWYSIWYGs: function (container) {
    return Sparkle.Components.Wysiwyg.attach(jQuery(container).find("[data-behaviour~=quill]"));
  },

  addToList: function (search_term, url) {
    jQuery.get({
      url: url,
      async: true,
      dataType: "script",
      data: "search=" + search_term,
    });
  },

  decorateFocusGroups(container) {
    const $focusGroups = jQuery(".form-focus-group", container);
    $focusGroups.has(".field_with_errors").length && $focusGroups.addClass("--invalid");

    function registerListeners(index, focusGroup) {
      const $focusGroup = jQuery(focusGroup);
      const $input = jQuery("input, [contenteditable]", $focusGroup);
      const handler = ({ type }) => $focusGroup.toggleClass("--focus", type === "focus");

      $input.off("focus.group");
      $input.off("blur.group");
      $input.on("focus.group", handler);
      $input.on("blur.group", handler);
    }
    $focusGroups.each(registerListeners);
  },

  updateReturnTo: function () {
    // Normalize so an incidental trailing slash (e.g. /pages/1/edit/ vs /pages/1/edit) doesn't
    // read as a different page and silently freeze the return_to at a stale tab state
    function normalizePathname(pathname) {
      return pathname.replace(/\/+$/, "");
    }

    jQuery("input[data-behaviour=return-to]").each(function () {
      var returnToField = jQuery(this);

      if (!returnToField.val()) {
        return;
      }

      var currentReturnUrl = new URL(returnToField.val(), window.location.origin);

      // Only update the return_to if the existing value is for the current page
      if (normalizePathname(currentReturnUrl.pathname) != normalizePathname(window.location.pathname)) {
        return;
      }

      returnToField.val(window.location.href);
    });
  },

  snapshot: function (form) {
    var data = [];

    // get all the inputs and their associated values (or checked status)
    jQuery(form)
      .find(FORM_CONTROL_SELECTOR)
      .each(function (index, element) {
        var $element = jQuery(element);
        var elementData = { element: $element };
        elementData.value = $element.val();
        if ($element.is('[type="radio"], [type="checkbox"]')) {
          elementData.checked = $element.is(":checked");
        }
        data.push(elementData);
      });

    return {
      restore: function () {
        // Restore will take the form data that is saved in var data and reapply
        // those feilds to the form set in snap shot. We do check to see if a
        // input is a checkbox or radio button. In those cases we set the previous
        // checked value, not the stored value.
        for (var index = 0; index < data.length; index++) {
          var input = data[index];
          var element = input.element;
          if (input.checked === undefined) {
            if (element.val() !== input.value) {
              element.val(input.value);
              element[0].dispatchEvent(new Event("change", { bubbles: true }));
            }
          } else {
            if (element.is(":checked") !== input.checked) {
              element.prop("checked", input.checked);
              element[0].dispatchEvent(new Event("change", { bubbles: true }));
            }
          }
        }
      },
    };
  },
});
